import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, BadgeAlert, CheckCircle, Info, X } from 'lucide-react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '../../theme/hooks'

type ConfirmModalProps = {
  type: 'warning' | 'error' | 'success' | 'info'
  isOpen: boolean
  title?: string
  message: string
  onConfirm: () => void
  onCancel: () => void
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
}

function ConfirmModal({
  type,
  isOpen,
  title = 'Confirmation',
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  isLoading = false,
}: ConfirmModalProps) {
  const { theme } = useTheme()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleConfirm = async () => {
    if (isProcessing || isLoading) return

    setIsProcessing(true)
    try {
      await onConfirm()
    } finally {
      setIsProcessing(false)
    }
  }

  const isBusy = isLoading || isProcessing

  if (!isOpen) return null

  const getStatusColors = () => {
    switch (type) {
      case 'warning':
        return {
          text: theme.status.warning.text,
          icon: theme.status.warning.icon,
        }
      case 'error':
        return {
          text: theme.status.error.text,
          icon: theme.status.error.icon,
        }
      case 'success':
        return {
          text: theme.status.success.text,
          icon: theme.status.success.icon,
        }
      case 'info':
        return {
          text: theme.status.info.text,
          icon: theme.status.info.icon,
        }
      default:
        return {
          text: theme.text.primary,
          icon: theme.text.primary,
        }
    }
  }

  const statusColors = getStatusColors()

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[999999] flex items-center justify-center"
        style={{ backgroundColor: theme.modal.overlay }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="w-full max-w-md rounded-lg shadow-xl"
          style={{
            backgroundColor: theme.modal.bg,
            boxShadow: theme.modal.shadow,
            willChange: 'transform', // ça permet d'éviter a chrome de flasher la page lors de l'animation
          }}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <div
            className="flex items-center justify-between border-b px-4 py-3"
            style={{ borderColor: theme.border.primary }}
          >
            <h2
              className="text-lg font-semibold"
              style={{ color: theme.text.primary }}
            >
              {title}
            </h2>
            <button
              onClick={onCancel}
              disabled={isBusy}
              className="transition-colors disabled:opacity-50"
              style={{
                color: theme.text.tertiary,
              }}
              onMouseEnter={e => {
                if (!isBusy) {
                  e.currentTarget.style.color = theme.text.secondary
                }
              }}
              onMouseLeave={e => {
                if (!isBusy) {
                  e.currentTarget.style.color = theme.text.tertiary
                }
              }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5">
            <div
              className="flex items-start"
              style={{ color: statusColors.text }}
            >
              {type === 'warning' && (
                <AlertTriangle
                  className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0"
                  style={{ color: statusColors.icon }}
                />
              )}
              {type === 'error' && (
                <BadgeAlert
                  className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0"
                  style={{ color: statusColors.icon }}
                />
              )}
              {type === 'success' && (
                <CheckCircle
                  className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0"
                  style={{ color: statusColors.icon }}
                />
              )}
              {type === 'info' && (
                <Info
                  className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0"
                  style={{ color: statusColors.icon }}
                />
              )}
              <p className="text-sm leading-relaxed">{message}</p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={onCancel}
                disabled={isBusy}
                className="rounded border px-4 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  backgroundColor: isBusy
                    ? theme.button.disabled.bg
                    : theme.button.secondary.bg,
                  color: isBusy
                    ? theme.button.disabled.text
                    : theme.button.secondary.text,
                  borderColor: isBusy
                    ? theme.button.disabled.border
                    : theme.button.secondary.border,
                }}
                onMouseEnter={e => {
                  if (!isBusy) {
                    e.currentTarget.style.backgroundColor =
                      theme.button.secondary.bgHover
                  }
                }}
                onMouseLeave={e => {
                  if (!isBusy) {
                    e.currentTarget.style.backgroundColor =
                      theme.button.secondary.bg
                  }
                }}
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                disabled={isBusy}
                className="flex items-center rounded px-4 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  backgroundColor: isBusy
                    ? theme.button.disabled.bg
                    : theme.button.danger.bg,
                  color: theme.button.danger.text,
                  borderColor: theme.button.danger.border,
                }}
                onMouseEnter={e => {
                  if (!isBusy) {
                    e.currentTarget.style.backgroundColor =
                      theme.button.danger.bgHover
                  }
                }}
                onMouseLeave={e => {
                  if (!isBusy) {
                    e.currentTarget.style.backgroundColor =
                      theme.button.danger.bg
                  }
                }}
                onMouseDown={e => {
                  if (!isBusy) {
                    e.currentTarget.style.backgroundColor =
                      theme.button.danger.bgActive
                  }
                }}
                onMouseUp={e => {
                  if (!isBusy) {
                    e.currentTarget.style.backgroundColor =
                      theme.button.danger.bgHover
                  }
                }}
              >
                {isBusy && (
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin"
                    style={{ color: theme.button.danger.text }}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {isBusy ? 'En cours...' : confirmText}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  )
}

export default ConfirmModal
